import { apiClient, type Query } from './apiClient';

/**
 * Shape of the backend's generated CRUD routes (backend: npm run make:module).
 * Note: BigInt columns (amounts, counters) arrive as strings, dates as ISO strings.
 */
export type ApiRecord = Record<string, unknown>;

export interface Pagination {
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface ListResponse<T> {
  data: T[];
  pagination: Pagination;
}

export interface ItemResponse<T> {
  data: T;
}

export interface ListQuery extends Query {
  limit?: number;
  offset?: number;
}

/** A single id, or every part of a composite key in route order, e.g. [campaignId, placementId]. */
export type ResourceKey = string | number | readonly (string | number)[];

const keyPath = (key: ResourceKey) =>
  (Array.isArray(key) ? key : [key]).map((part) => encodeURIComponent(String(part))).join('/');

export function createResourceApi<T = ApiRecord, CreateInput = Partial<T>, UpdateInput = Partial<CreateInput>>(path: string) {
  return {
    list: (query?: ListQuery, signal?: AbortSignal) => apiClient.get<ListResponse<T>>(path, { query, signal }),
    get: (key: ResourceKey, signal?: AbortSignal) => apiClient.get<ItemResponse<T>>(`${path}/${keyPath(key)}`, { signal }),
    create: (input: CreateInput) => apiClient.post<ItemResponse<T>>(path, input),
    update: (key: ResourceKey, input: UpdateInput) => apiClient.patch<ItemResponse<T>>(`${path}/${keyPath(key)}`, input),
    remove: (key: ResourceKey) => apiClient.delete(`${path}/${keyPath(key)}`),
  };
}
